import { useQuery } from 'react-query';
import { useHttp } from 'hooks/use-fetch';
import { fetchRatingScaleTimeseries } from 'pages/Dashboard/services/tracking.services';
import * as React from 'react';
import { Box, Tabs, Tab, Typography, Grid, Chip, Link, ToggleButtonGroup, ToggleButton } from '@mui/material';
import PatientDetailsCard, { LoadingState, NoDataState } from 'components/PatientDetailsCard';
import PatientScaleDetailsDialog from 'pages/ScalesManifest/PatientScaleDetailsDialog';
import RatingScaleAreaChart from 'pages/Dashboard/components/RatingScaleAreaChart';
import { Category, CategoryResult, Scale } from 'pages/Dashboard/types/whoiam.types';
import { whoIam } from 'utils/apiUrls';
import { fetchWhoIamCategoryResults } from 'pages/Dashboard/services/whoiam.services';
import { format } from 'date-fns';
import { pink } from 'theme';
import { googleDateToDateString } from 'utils/dateUtils';
import { TableData } from 'pages/ScalesManifest/types/scales-manifest.types';
import { PatientObj } from 'pages/Dashboard/types/patient.types';
import { capitalize } from 'utils/appUtils';


interface RatingScaleCardProps {
  ratingScales: Category[];
  timeFrame?: string;
  patient?: PatientObj;
  isLoadingScales?: boolean;
  onChangeTimeFrame?: (timeFrame: string) => void;
  gridSize?: number;
}

function RatingScaleCard({
  gridSize = 12,
  patient,
  ratingScales,
  timeFrame,
  isLoadingScales,
  onChangeTimeFrame,
}: RatingScaleCardProps) {
  // State for selected segment (main scale or subscale)
  const [selectedSegment, setSelectedSegment] = React.useState<number>(0);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const { http, providerId } = useHttp();
  const patientId = patient?.id;

  const [tab, setTab] = React.useState(0);
  const [selectedScale, setSelectedScale] = React.useState({} as Scale);
  const scale = ratingScales[tab];
  const isPlottable = scale.plottable;

  // Fetch timeseries data for the selected rating scale
  const { data: timeseriesData, isLoading: isLoadingTimeseries } = useQuery(
    ['ratingScaleTimeseries', providerId, patientId, scale?.id, timeFrame, isPlottable],
    fetchRatingScaleTimeseries(http.get),
    { enabled: !!scale?.id && !!providerId && !!patientId },
  );

  const isMainScalePlottable = timeseriesData?.scaleMetadata?.ranges?.length;
  const areSubscalesPlottable = timeseriesData?.scaleMetadata?.subscale?.length;
  const hasTimeseriesData = timeseriesData && timeseriesData?.results?.length > 0;

  const scaleResult = useQuery(
    [`${whoIam.categoryResults.queryUrl}-${scale?.id}`, providerId, patientId, scale?.id, isPlottable],
    {
      queryFn: fetchWhoIamCategoryResults(http.get),
      enabled: !!scale?.id && !!providerId && !!patientId,
    },
  );

  const scaleResultData = React.useMemo(() => scaleResult.data?.results ?? [], [scaleResult.data]);

  React.useEffect(() => {
    if (scale?.id && scaleResultData.length > 0) {
      const selectedScaleDetails = {
        categoryId: scale.id,
        results: scaleResultData,
      };
      setSelectedScale({
        ...(selectedScaleDetails.results?.[0] || ({} as CategoryResult)),
        categoryId: selectedScaleDetails.categoryId,
        scale: scale.abbreviation || scale.name,
      });
    }
  }, [scale, scaleResultData]);

  const latestAttemptedAt = React.useMemo(() => {
    if (timeseriesData && (timeseriesData.results?.length ?? 0) > 0) {
      return [...timeseriesData.results][timeseriesData.results.length - 1]?.attemptedAt;
    }
    return selectedScale?.attemptedAt || '';
  }, [timeseriesData, selectedScale]);

  const selectedRow = {
    attemptId: selectedScale?.attemptId,
    categoryId: selectedScale?.categoryId,
    takenOn: latestAttemptedAt ? format(new Date(latestAttemptedAt), 'do MMM yyyy') : '',
    metaInfo: selectedScale.metaInfo,
    score: `${selectedScale?.score}/${selectedScale?.totalScore}`,
  };

  const metaObject = selectedRow?.metaInfo ? JSON.parse(selectedRow?.metaInfo ?? '{}') ?? {} : {};

  const keys = metaObject?.orderKey?.length ? metaObject.orderKey : Object.keys(metaObject);

  const subscores = keys.reduce((acc: React.ReactElement[], key: string) => {
    if (!metaObject?.orderKey?.length && key === 'notes') return acc;
    // Try to extract score from value, fallback to value itself
    const value = metaObject[key];
    acc.push(
      <Grid
        item
        key={key}
        sx={{
          flex: '1 1 0',
          minWidth: 120,
          maxWidth: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='body1'
          color='text.primary'
          fontWeight={600}
          align='center'
          sx={{ mb: 0.5, wordBreak: 'break-word' }}
        >
          {key}
        </Typography>
        <Typography
          variant='body1'
          color='text.primary'
          fontWeight={400}
          align='center'
          sx={{ wordBreak: 'break-word' }}
        >
          {value}
        </Typography>
      </Grid>,
    );
    return acc;
  }, []);

  // Prepare segments for main scale and subscales
  const segments = [];
  if (isMainScalePlottable) {
    segments.push({
      label: scale.abbreviation || scale.name,
      ranges: timeseriesData?.scaleMetadata?.ranges || [],
      key: null, // main scale
    });
  }
  if (areSubscalesPlottable) {
    timeseriesData?.scaleMetadata?.subscale.forEach((sub) => {
      segments.push({
        label: sub.title,
        ranges: sub.ranges,
        key: sub.key,
      });
    });
  }

  React.useEffect(() => {
    if (segments.length > 0) {
      if (selectedSegment >= segments.length || selectedSegment < 0) {
        setSelectedSegment(0);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments.length]);

  // Prepare chart data and ranges for selected segment
  let chartData = timeseriesData?.results || [];
  const chartRanges = segments[selectedSegment]?.ranges || [];
  if (segments[selectedSegment]?.key) {
    // Subscale: extract score from metaInfo
    const subKey = segments[selectedSegment].key as unknown as string;
    chartData = (timeseriesData?.results || []).map((r) => {
      let score = 0;
      try {
        const meta = typeof r.metaInfo === 'string' ? JSON.parse(r.metaInfo) : {};
        const val = Number(meta[subKey]);
        score = !isNaN(val) ? val : 0;
      }
      catch {
        score = 0;
      }
      return {
        ...r,
        score,
      };
    });
  }

  const shouldShowNoDataState = (isPlottable
      && !isLoadingTimeseries
      && ((isMainScalePlottable && !hasTimeseriesData)
        || (!isMainScalePlottable && !!areSubscalesPlottable && !hasTimeseriesData)
        || (hasTimeseriesData
          && segments[selectedSegment]?.key
          && (!chartRanges || chartRanges.length === 0))))
    || (!isPlottable
      && !isLoadingScales
      && !isLoadingTimeseries
      && scaleResultData.length > 0
      && !selectedRow.score);

  const isLoading = isPlottable && isLoadingTimeseries;

  return (
    <PatientDetailsCard
      title='Rating scale'
      gridProps={{ item: true, xs: 12, md: gridSize }}
      selectTimeFrame={timeFrame}
      isLoading={isLoadingScales}
      noData={ratingScales.length === 0}
      onTimeFrameChange={onChangeTimeFrame}
      showSubHeader={!!isPlottable}
    >
      <Box flex={1} display='flex' flexDirection='column'>
        <Box sx={{ overflowX: 'auto', width: '100%' }}>
          <Tabs
            value={tab}
            onChange={(__, v) => setTab(v)}
            aria-label='rating scale tabs'
            variant='scrollable'
            scrollButtons='auto'
            sx={{ minHeight: 48 }}
          >
            {ratingScales.map((tabScale, idx) => (
              <Tab
                key={tabScale.id}
                label={tabScale.abbreviation || tabScale.name}
                sx={{ minWidth: 120, fontWeight: 600, whiteSpace: 'nowrap' }}
              />
            ))}
          </Tabs>
        </Box>
        {/* Unified no data state for all cases */}
        {shouldShowNoDataState && (
          <Box flex={1} display='flex' flexDirection='column'>
            <NoDataState />
          </Box>
        )}
        {/* Show main scale score above subscale segment buttons if only subscales are plottable */}
        {isPlottable && !isMainScalePlottable && !!areSubscalesPlottable && (
          <Box
            display='flex'
            flexDirection='column'
            alignItems='flex-start'
            justifyContent='center'
            width='100%'
            pt={2.5}
          >
            <Typography variant='body1' fontWeight={600}>
              Score
            </Typography>
            <Typography variant='body1' fontWeight={400}>
              {(() => {
                // Use the latest result's score/totalScore
                if (timeseriesData?.results?.length) {
                  const latest = timeseriesData.results[timeseriesData.results.length - 1];
                  return latest
                    && typeof latest.score !== 'undefined'
                    && typeof latest.totalScore !== 'undefined'
                    ? `${latest.score}/${latest.totalScore}`
                    : '-';
                }
                return '-';
              })()}
            </Typography>
          </Box>
        )}
        {/* MUI Segmented ToggleButtonGroup for main/subscales, horizontally scrollable */}
        {segments.length > 0 && !!areSubscalesPlottable && (
          <Box sx={{ mt: 2.5, width: '100%' }}>
            <ToggleButtonGroup
              value={selectedSegment}
              exclusive
              onChange={(_, idx) => typeof idx === 'number' && setSelectedSegment(idx)}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                width: '100%',
                rowGap: 1,
              }}
            >
              {segments.map((seg, idx) => (
                <ToggleButton
                  key={seg.label}
                  value={idx}
                  sx={(t) => ({
                    py: 1,
                    px: 2.75,
                    borderColor: t.palette.primary.main,
                    color: t.palette.primary.main,
                    whiteSpace: 'nowrap',
                    minWidth: 120,
                    fontWeight: 600,
                    '&.Mui-selected': {
                      color: t.palette.primary.main,
                    },
                    // Update style for .MuiToggleButtonGroup-grouped:not(:first-of-type)
                    '&.MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                      borderLeft: `1px solid ${t.palette.primary.main}`,
                    },
                  })}
                >
                  {seg.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}
      </Box>
      {!shouldShowNoDataState && !isLoading && selectedRow?.takenOn && (
        <Typography variant='caption' color='text.secondary' mt={2.5}>
          Last updated on
          {' '}
          {selectedRow?.takenOn}
          {' '}
        </Typography>
      )}
      {segments[selectedSegment]?.key
        && (!chartRanges || chartRanges.length === 0)
        && hasTimeseriesData
        && !isLoadingTimeseries && (
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            width='100%'
            py={6}
          >
            <Typography variant='h4' fontWeight={700} align='center' gutterBottom>
              Score
            </Typography>
            <Typography variant='h5' fontWeight={400} align='center' gutterBottom>
              {(() => {
                const subKey = segments[selectedSegment].key as unknown as string;
                const latest = timeseriesData.results[timeseriesData.results.length - 1];
                if (latest && latest.metaInfo) {
                  try {
                    const meta = typeof latest.metaInfo === 'string' ? JSON.parse(latest.metaInfo) : {};
                    return meta[subKey] ?? '-';
                  }
                  catch {
                    return '-';
                  }
                }
                return '-';
              })()}
            </Typography>
          </Box>
      )}
      {/* Otherwise, show chart as usual */}
      {isPlottable
        && hasTimeseriesData
        && !isLoadingTimeseries
        && (!segments[selectedSegment]?.key || (chartRanges && chartRanges.length > 0)) && (
          <Box>
            <Chip
              label={`Avg score: ${(() => {
                if (chartData?.length) {
                  const sum = chartData.reduce(
                    (acc, cur) => acc + (typeof cur.score === 'number' ? cur.score : Number(cur.score)),
                    0,
                  );
                  const avg = sum / chartData.length;
                  // For subscales, no totalScore; for main, use first result's totalScore
                  const totalScore = segments[selectedSegment]?.key
                    ? ''
                    : chartData[0]?.totalScore || '';
                  return `${Math.round(avg * 100) / 100}${totalScore ? `/${totalScore}` : ''}`;
                }
                return selectedRow.score;
              })()}`}
              sx={{
                bgcolor: pink[50],
                color: pink.Main,
                my: 2,
              }}
              size='small'
            />
            <RatingScaleAreaChart data={chartData} ranges={chartRanges} timeFrame={timeFrame} />
          </Box>
      )}
      {isLoading && <LoadingState />}
      {!isPlottable && scaleResultData.length > 0 && !isLoadingScales && !isLoadingTimeseries && (
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          width='100%'
          py={6}
        >
          <Typography variant='h4' fontWeight={700} align='center' gutterBottom>
            Score
          </Typography>
          <Typography variant='h5' fontWeight={400} align='center' gutterBottom>
            {selectedRow.score}
          </Typography>
          <Grid
            container
            spacing={0}
            justifyContent='center'
            alignItems='flex-start'
            wrap='wrap'
            mt={4}
            mb={2}
            sx={{ rowGap: 4, columnGap: '48px' }}
          >
            {subscores}
          </Grid>
          <Link
            component='button'
            variant='body1'
            color='primary'
            underline='hover'
            sx={{ mt: 2, fontWeight: 500, fontSize: 18 }}
            onClick={() => setDetailsOpen(true)}
          >
            view all answers
          </Link>
          <PatientScaleDetailsDialog
            selectedRow={
              {
                attemptId: detailsOpen ? selectedScale?.attemptId : '',
                categoryId: selectedScale?.categoryId,
                takenOn: selectedScale?.attemptedAt
                  ? format(new Date(selectedScale?.attemptedAt), 'M/d/yyyy')
                  : '',
                metaInfo: selectedScale.metaInfo,
                score: `${selectedScale?.score}/${selectedScale?.totalScore}`,
                scale: selectedScale?.scale,
                patientName: patient?.name
                  ? `${capitalize(patient.name.firstName ?? '')} ${capitalize(
                    patient.name.lastName ?? '',
                  )}`
                  : '',
                patientDOB: patient?.dateOfBirth ? googleDateToDateString(patient.dateOfBirth) : '',
                providerName: patient?.provider
                  ? `${capitalize(patient.provider.firstName ?? '')} ${capitalize(
                    patient.provider.lastName ?? '',
                  )}`
                  : '',
              } as unknown as TableData
            }
            setSelectedRow={() => setDetailsOpen(false)}
            showNotes
          />
        </Box>
      )}
    </PatientDetailsCard>
  );
}


export default RatingScaleCard;
